import React, { useState, useCallback } from 'react';
import { useShallowEqualSelector } from '../../../../../hooks/redux';
import BooksLoginRequireAlert from './components/BooksLoginRequireAlert';
import {
  CreateUserQuery,
  LoginQuery,
  LoginWithFbQuery,
  useAuth,
} from '../../../../../hooks/api/auth';
import { setMeToBookInfo } from '../../../../../redux/actions/merchants/resources/books';
import InputCreateUser from '../../../../../molecules/merchant/resources/book/InputCreateUser';
import InputLogin from '../../../../../molecules/merchant/resources/book/InputLogin';
import { ReduxState } from '../../../../../redux/reducers';
import { getMerchantResourcesBooksInputs } from '../../../../../redux/selectors/merchants/resources/books/inputs';
import CreateUserAlert from '../../../../../molecules/common/CreateUserAlert';
import { useAppDispatch } from '../../../../../redux';
import { ErrorType } from '../../../../../domain/entities/Auth';

interface ElementProps {
  onRestart: () => void;
  renew?: boolean;
  onJustCreateUser?: () => void;
  resourcePublicId: string;
}

enum ViewType {
  Login = 'login',
  CreateUser = 'createUser',
}

type Props = ElementProps;

const InputRequireLogin: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();

  const { login, loginWithFb, createUser } = useAuth();
  const [errors, setErrors] = useState<ErrorType[]>();

  const { onJustCreateUser, resourcePublicId } = props;
  const useSelector = () =>
    useShallowEqualSelector((state: ReduxState) => ({
      inputs: getMerchantResourcesBooksInputs(
        state.merchantsResourcesBooksInputs,
      ),
    }));
  const { inputs } = useSelector();

  const [viewType, setViewType] = useState<ViewType>(ViewType.Login);
  const [postable, setPostable] = useState(true);

  const handleSwitchView = useCallback((viewType: ViewType) => {
    setViewType(viewType);
  }, []);

  const handleLogin = useCallback(
    async (_q: LoginQuery) => {
      setPostable(false);
      login(_q.email, _q.password)
        .then(async ({ errorType }) => {
          if (errorType) return setErrors([errorType]);

          await dispatch(setMeToBookInfo(inputs.merchantId!, resourcePublicId));
        })
        .catch((_err) => {
          return;
        })
        .finally(() => setPostable(true));
    },
    [dispatch, inputs.merchantId, login, resourcePublicId],
  );

  const handleLoginWithFb = useCallback(
    async (query: LoginWithFbQuery) => {
      setPostable(false);
      loginWithFb(query)
        .then(async ({ errorType }) => {
          if (errorType) return setErrors([errorType]);

          await dispatch(setMeToBookInfo(inputs.merchantId!, resourcePublicId));
        })
        .catch((_err) => {
          return;
        })
        .finally(() => setPostable(true));
    },
    [dispatch, inputs.merchantId, loginWithFb, resourcePublicId],
  );

  const handleCreateUser = useCallback(
    async (query: CreateUserQuery) => {
      setPostable(false);
      createUser(query)
        .then(async ({ errorType }) => {
          if (errorType) return setErrors([errorType]);

          onJustCreateUser?.();
          await dispatch(setMeToBookInfo(inputs.merchantId!, resourcePublicId));
        })
        .catch((_err) => {
          return;
        })
        .finally(() => setPostable(true));
    },
    [
      createUser,
      dispatch,
      inputs.merchantId,
      onJustCreateUser,
      resourcePublicId,
    ],
  );

  if (viewType === ViewType.Login) {
    return (
      <>
        <BooksLoginRequireAlert
          onClick={() => handleSwitchView(ViewType.CreateUser)}
        />
        <InputLogin
          postable={postable}
          onLogin={(query: LoginQuery) => handleLogin(query)}
          onLoginWithFb={(query: LoginWithFbQuery) => handleLoginWithFb(query)}
          errors={errors}
        />
      </>
    );
  }

  return (
    <>
      <CreateUserAlert onClick={() => handleSwitchView(ViewType.Login)} />
      <InputCreateUser
        postable={postable}
        onSubmit={(query: CreateUserQuery) => handleCreateUser(query)}
        errors={errors}
      />
    </>
  );
};

export default InputRequireLogin;
